import type {
  DocumentReferenceCallout,
  DocumentReferenceCaption,
  DocumentReferenceSectionContent,
  DocumentReferenceStructuredBlock,
  DocumentReferenceStructuredContentPayload,
  DocumentReferenceStructuredItem,
  DocumentReferenceStructuredSection,
  DocumentReferenceTableRow,
} from './document-reference-types';

export interface PresentedDocumentReferenceCaption {
  readonly heading: string;
  readonly timestamp?: string;
  readonly detail?: string;
}

export interface PresentedDocumentReferenceBlock {
  readonly id?: string;
  readonly styleCode?: string;
  readonly leadingCallouts: ReadonlyArray<DocumentReferenceCallout>;
  readonly inlineCallouts: ReadonlyArray<DocumentReferenceCallout>;
  readonly trailingCallouts: ReadonlyArray<DocumentReferenceCallout>;
  readonly sections: ReadonlyArray<DocumentReferenceStructuredSection>;
}

export interface PresentedDocumentReferenceItem {
  readonly caption: PresentedDocumentReferenceCaption;
  readonly blocks: ReadonlyArray<PresentedDocumentReferenceBlock>;
  readonly footnotes: ReadonlyArray<DocumentReferenceCallout>;
}

export interface PresentedDocumentReferenceDocument {
  readonly items: ReadonlyArray<PresentedDocumentReferenceItem>;
  readonly footnotes: ReadonlyArray<DocumentReferenceCallout>;
}

export const presentDocumentReferenceDocument = (
  document: DocumentReferenceStructuredContentPayload,
): PresentedDocumentReferenceDocument => ({
  items: document.items.map((item) => presentDocumentReferenceItem(item)),
  footnotes: document.footnotes,
});

export const presentDocumentReferenceItem = (
  item: DocumentReferenceStructuredItem,
): PresentedDocumentReferenceItem => ({
  caption: presentDocumentReferenceCaption(item.caption),
  blocks: item.blocks.map((block) => presentDocumentReferenceBlock(block)),
  footnotes: item.footnotes,
});

const presentDocumentReferenceCaption = (
  caption: DocumentReferenceCaption,
): PresentedDocumentReferenceCaption => {
  const parts = [caption.author, caption.credentials].filter(Boolean);
  const heading =
    parts.length > 0 ? parts.join(', ') : (caption.raw ?? 'Document Entry');
  const timestamp = buildCaptionTimestamp(caption);
  const detail =
    !timestamp && caption.raw && caption.raw !== heading
      ? caption.raw
      : undefined;

  return {
    heading,
    timestamp,
    detail,
  };
};

const buildCaptionTimestamp = (
  caption: DocumentReferenceCaption,
): string | undefined => {
  if (caption.timestampIso) {
    const parsed = new Date(caption.timestampIso);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  }

  return caption.timestampRaw;
};

const presentDocumentReferenceBlock = (
  block: DocumentReferenceStructuredBlock,
): PresentedDocumentReferenceBlock => ({
  id: block.id,
  styleCode: block.styleCode,
  leadingCallouts: block.callouts.filter(
    (callout) => callout.placement === 'leading',
  ),
  inlineCallouts: block.callouts.filter(
    (callout) => callout.placement === 'inline',
  ),
  trailingCallouts: block.callouts.filter(
    (callout) => callout.placement === 'trailing',
  ),
  sections: block.sections
    .map((section) => presentDocumentReferenceSection(section))
    .filter(
      (section): section is DocumentReferenceStructuredSection =>
        section !== undefined,
    ),
});

const presentDocumentReferenceSection = (
  section: DocumentReferenceStructuredSection,
): DocumentReferenceStructuredSection | undefined => {
  const contents = section.contents
    .map((content) => presentDocumentReferenceSectionContent(content))
    .filter(
      (content): content is DocumentReferenceSectionContent =>
        content !== undefined,
    );

  if (contents.length === 0) return undefined;

  return {
    heading: section.heading,
    headingSource: section.headingSource,
    contents,
  };
};

const presentDocumentReferenceSectionContent = (
  content: DocumentReferenceSectionContent,
): DocumentReferenceSectionContent | undefined => {
  if (content.kind !== 'table') return content;

  const rows = content.rows.filter(shouldPresentTableRow);
  if (rows.length === 0) return undefined;

  return {
    kind: 'table',
    rows,
  };
};

const shouldPresentTableRow = (row: DocumentReferenceTableRow): boolean => {
  const label = row.test.trim().toLowerCase();
  if (/pathologist\s*signature/.test(label)) return false;
  if (/analysis\s*time/.test(label)) return false;

  return true;
};
